import type { ComponentInit } from "./registry";

const SHOULDER = "--fa-divider-shoulder";
const ATTRIBUTE = "data-fa-divider-shoulder";
const BOUNDARY = new Set(["MAIN", "ARTICLE", "BODY"]);
const ADJACENT = 2;
const PANEL = '[role="tabpanel"]';

type Direction = {
	sibling: (node: Element) => Element | null;
	gap: (neighbour: DOMRect, root: DOMRect) => number;
	ground: string;
};

const DIRECTIONS: Record<string, Direction> = {
	above: {
		sibling: (node) => node.previousElementSibling,
		gap: (neighbour, root) => neighbour.bottom - root.top,
		ground: "--fa-ground-end",
	},
	below: {
		sibling: (node) => node.nextElementSibling,
		gap: (neighbour, root) => neighbour.top - root.bottom,
		ground: "--fa-ground-start",
	},
};

const alphaOf = (colour: string): number => {
	const body = /^rgba?\(([^)]+)\)/.exec(colour.trim())?.[1];

	if (!body) return 0;

	const alpha = body.split(/[\s,/]+/).filter(Boolean)[3];

	return alpha === undefined ? 1 : Number.parseFloat(alpha);
};

const paints = (colour: string): boolean => alphaOf(colour) > 0;

const neighbourOf = (root: HTMLElement, direction: Direction): HTMLElement | null => {
	let node: Element = root;

	while (!direction.sibling(node)) {
		const parent = node.parentElement;

		if (!parent || BOUNDARY.has(parent.tagName)) return null;

		node = parent;
	}

	const neighbour = direction.sibling(node);

	if (!(neighbour instanceof HTMLElement)) return null;

	const box = neighbour.getBoundingClientRect();

	/* An unmeasurable box reads 0 against a root that also reads 0, which passes the gap test. */
	if (box.height === 0) return null;

	return Math.abs(direction.gap(box, root.getBoundingClientRect())) <= ADJACENT
		? neighbour
		: null;
};

const pageGround = (root: HTMLElement): string | null => {
	let node = root.parentElement;

	while (node) {
		const colour = getComputedStyle(node).backgroundColor;

		if (paints(colour)) return colour;

		node = node.parentElement;
	}

	return null;
};

const groundOf = (element: HTMLElement, direction: Direction): string | null => {
	const styles = getComputedStyle(element);
	const declared = styles.getPropertyValue(direction.ground).trim();

	if (paints(declared)) return declared;
	if (paints(styles.backgroundColor)) return styles.backgroundColor;

	return null;
};

const resolve = (root: HTMLElement, direction: Direction): string | null => {
	const neighbour = neighbourOf(root, direction);

	if (neighbour) {
		const own = groundOf(neighbour, direction);

		if (own) return own;

		const inner = neighbour.matches(PANEL) ? neighbour.firstElementChild : null;
		const nested = inner instanceof HTMLElement ? groundOf(inner, direction) : null;

		if (nested) return nested;
	}

	return pageGround(root);
};

export const initDividerShoulder: ComponentInit = (root) => {
	const direction = DIRECTIONS[root.getAttribute(ATTRIBUTE) ?? ""];

	if (!direction) return;
	if (root.style.getPropertyValue(SHOULDER)) return;

	const resolved = resolve(root, direction);

	if (!resolved) return;

	root.style.setProperty(SHOULDER, resolved);

	return () => {
		root.style.removeProperty(SHOULDER);

		/* An emptied `style` attribute still prints in the HTML panel the vendor copies. */
		if (!root.getAttribute("style")) root.removeAttribute("style");
	};
};
